import {
    Container,
    Sprite,
    Texture,
    NineSlicePlane,
} from 'pixi.js';
import { UIBoxBG } from './UIBoxBG';
import { GAMEPLAY } from '../../constants';
import { Focusable, fonts, StyledBitmapText } from '../../ui';
import { LoadContext } from '../../utils';

// width for focus frame
const FRAME_WIDTH = 6;

export class BatteryMeter extends Container implements Focusable
{
    private bg: UIBoxBG;
    private label: StyledBitmapText;
    private _level: number;
    private emptyTex: Texture;
    private fullTex: Texture;
    private charges: Sprite[];
    private focusBox: NineSlicePlane;
    /**
     * If this should be skipped in the focus manager.
     */
    public skip: boolean;

    public static preload()
    {
        const loader = LoadContext.current;
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');
        loader.add('SharedUI', './assets/shared/SharedUI{size}.json');

        UIBoxBG.preload();
    }

    constructor()
    {
        super();

        const loader = LoadContext.current;

        this.bg = new UIBoxBG(625, 110);
        this.addChild(this.bg);

        // text style will be updated automatically
        this.label = new StyledBitmapText('', fonts.GameLabel);
        // expected max width: 207
        this.label.anchor.y = 0.6;
        this.label.position.set(20, 55);
        this.addChild(this.label);

        this.fullTex = loader.getSpritesheet('GameAtlas').textures['battery0002'];
        this.emptyTex = loader.getSpritesheet('GameAtlas').textures['battery0001'];

        this.charges = [];
        const chargeSpacing = 15; // originally 18, needs to be narrower
        for (let i = 0; i < GAMEPLAY.MAX_BATTERY; ++i)
        {
            const charge = new Sprite(this.emptyTex);
            charge.width = chargeSpacing - 1;
            charge.position.set(240 + chargeSpacing * i, 35);
            this.addChild(charge);
            this.charges.push(charge);
        }

        this.focusBox = new NineSlicePlane(loader.getSpritesheet('SharedUI').textures['highlight_frame'], FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH);
        const {width, height} = this.bg;
        // make box just a tad bigger than whatever the size is
        this.focusBox.width = width + 2;
        this.focusBox.height = height + 2;
        this.focusBox.position.set(-1, -1);
        this.addChild(this.focusBox);
        this.focusBox.visible = false;

        this.level = GAMEPLAY.STARTING_BATTERY;

        this.interactive = true;
    }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public activate()
    {
        // no op here
    }

    public get level() { return this._level; }
    public set level(level)
    {
        if (level < 0)
        {
            level = 0;
        }
        else if (level > GAMEPLAY.MAX_BATTERY)
        {
            level = GAMEPLAY.MAX_BATTERY;
        }
        this._level = level;
        this.label.text = `Battery Life: ${level}`;
        for (let i = 0; i < this.charges.length; ++i)
        {
            this.charges[i].texture = level > i ? this.fullTex : this.emptyTex;
        }
    }

    public destroy()
    {
        super.destroy({children: true});
    }
}